import { POWER_WEIGHTS } from "@mpl/shared";
import type { RouterOutput } from "../lib/trpc.js";
import { ManagerLink, Num } from "./ui.js";
import DataTable, { type Column } from "./DataTable.js";

type Power = NonNullable<RouterOutput["league"]["standings"]["power"]>;
type Row = Power["ratings"][number];

/**
 * The composite power rating, with its working shown.
 *
 * The number on its own invites the obvious argument — "how am I below him?" —
 * so every part that went into it is on the row next to it. The maths lives in
 * `computePowerRatings`; this only formats it.
 */
export default function PowerRatings({ power }: { power: Power }) {
  const { basis } = power;
  const showDivision = basis.hasDivisions;

  const columns: Column<Row>[] = [
    {
      key: "position",
      label: "#",
      compact: true,
      sortBy: (r) => r.position,
      className: "text-chalk-500",
      render: (r) => r.position,
    },
    {
      key: "name",
      label: "Manager",
      compact: true,
      primary: true,
      sortBy: (r) => r.name,
      className: "font-semibold",
      render: (r) => <ManagerLink slug={r.slug} name={r.name} />,
    },
    {
      key: "record",
      label: `Record /${POWER_WEIGHTS.record}`,
      align: "right",
      title: "Win percentage, at full weight",
      sortBy: (r) => r.recordPoints,
      render: (r) => <Num value={r.recordPoints.toFixed(1)} muted />,
    },
    {
      key: "diff",
      label: `Diff /${POWER_WEIGHTS.differential}`,
      align: "right",
      title: "Points per game, clamped at 21 either way",
      sortBy: (r) => r.differentialPoints,
      render: (r) => (
        <Num
          value={r.differentialPoints.toFixed(1)}
          muted
        />
      ),
    },
    {
      key: "schedule",
      label: `Sched /${POWER_WEIGHTS.schedule}`,
      align: "right",
      title: "Your opponents' win percentage this season, over the games played",
      sortBy: (r) => r.schedulePoints,
      render: (r) => <Num value={r.schedulePoints.toFixed(1)} muted />,
    },
    ...(showDivision
      ? [
          {
            key: "division",
            label: `Div /${POWER_WEIGHTS.division}`,
            align: "right" as const,
            title: "Position within your own division",
            sortBy: (r: Row) => r.divisionPoints ?? -1,
            render: (r: Row) => (
              <Num value={(r.divisionPoints ?? 0).toFixed(1)} muted />
            ),
          },
        ]
      : []),
    {
      key: "rating",
      label: "Total",
      align: "right",
      compact: true,
      title: "Out of 100",
      sortBy: (r) => r.rating,
      render: (r) => <Num value={r.rating.toFixed(1)} />,
    },
  ];

  return (
    <DataTable
      rows={power.ratings}
      columns={columns}
      rowKey={(r) => r.managerId}
      initialSort={{ key: "rating", dir: "desc" }}
      minWidth={620}
      footnote={
        <div className="space-y-2">
          <p>
            Record {POWER_WEIGHTS.record}%, point differential{" "}
            {POWER_WEIGHTS.differential}% (capped at 21 a game so a blowout
            can't be farmed), strength of schedule {POWER_WEIGHTS.schedule}%
            {showDivision
              ? `, divisional rank ${POWER_WEIGHTS.division}%`
              : ""}
            .
          </p>
          <p>
            Strength of schedule is your opponents' combined win percentage this
            season, over the games you've played — the same figure the standings
            use as a tiebreaker.
          </p>
          {!showDivision && (
            <p>
              This competition ran as a single table, so the divisional part is
              dropped and the other three are scaled back up to 100 — otherwise
              it would just be the record counted twice.
            </p>
          )}
          {basis.preseason && (
            <p className="text-chalk-400">
              <strong className="text-chalk-300">Nothing has been played yet.</strong>{" "}
              Every part is level until the first result comes in — the schedule
              counts as .500 for everyone
              {showDivision ? ", and every manager shares their division's places" : ""}
              . It will start moving in Week 1.
            </p>
          )}
        </div>
      }
    />
  );
}
